var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import { FormControlController } from '../../internal/controllers/form.js';
import { defaultValue } from '../../internal/default-value.js';
import { SgdfElement } from '../../internal/sgdf-element.js';
import { watch } from '../../internal/watch.js';
import styles from './RoundCheckbox.styles.js';
let id = 0;
/**
 * @since 2.0
 * @status stable
 *
 * @slot - The checkbox's label.
 *
 * @event sgdf-blur - Emitted when the control loses focus.
 * @event sgdf-change - Emitted when the control's checked state changes.
 * @event sgdf-focus - Emitted when the control gains focus.
 *
 * @csspart label - The checkbox label.
 *
 * @cssproperty --checkbox-fill-color - To set the fill color, default: "currentColor"
 */
export class SgdfRoundCheckbox extends SgdfElement {
    constructor() {
        super(...arguments);
        this.formControlController = new FormControlController(this, {
            value: (control) => control.checked ? control.value || 'on' : undefined,
            defaultValue: (control) => control.defaultChecked,
            setValue: (control, checked) => (control.checked = checked),
        });
        this.hasFocus = false;
        this.title = ''; // make reactive to pass through
        /** The name of the checkbox, submitted as a name/value pair with form data. */
        this.name = '';
        /** The checkbox's size. */
        this.size = 'medium';
        /** Disables the checkbox. */
        this.disabled = false;
        /** Draws the checkbox in a checked state. */
        this.checked = false;
        /**
         * Draws the checkbox in an indeterminate state. This is usually applied to checkboxes that represents a "select
         * all/none" behavior when associated checkboxes have a mix of checked and unchecked states.
         */
        this.indeterminate = false;
        /** The default value of the form control. Primarily used for resetting the form control. */
        this.defaultChecked = false;
        /**
         * By default, form controls are associated with the nearest containing `<form>` element. This attribute allows you
         * to place the form control outside of a form and associate it with the form that has this `id`. The form must be in
         * the same document or shadow root for this to work.
         */
        this.form = '';
        /** Makes the checkbox a required field. */
        this.required = false;
        this.inputId = `round-checkbox-${++id}`;
        this.labelId = `round-checkbox-label-${id}`;
    }
    /** Gets the validity state object */
    get validity() {
        return this.input.validity;
    }
    /** Gets the validation message */
    get validationMessage() {
        return this.input.validationMessage;
    }
    firstUpdated() {
        this.formControlController.updateValidity();
    }
    handleClick() {
        this.checked = !this.checked;
        this.indeterminate = false;
        this.emit('sgdf-change');
    }
    handleBlur() {
        this.hasFocus = false;
        this.emit('sgdf-blur');
    }
    handleInput() {
        this.emit('sgdf-input');
    }
    handleInvalid(event) {
        this.formControlController.setValidity(false);
        this.formControlController.emitInvalidEvent(event);
    }
    handleFocus() {
        this.hasFocus = true;
        this.emit('sgdf-focus');
    }
    handleDisabledChange() {
        // Disabled form controls are always valid
        this.formControlController.setValidity(this.disabled);
    }
    handleStateChange() {
        this.input.checked = this.checked; // force a sync update
        this.input.indeterminate = this.indeterminate; // force a sync update
        this.formControlController.updateValidity();
    }
    /** Simulates a click on the checkbox. */
    click() {
        this.input.click();
    }
    /** Sets focus on the checkbox. */
    focus(options) {
        this.input.focus(options);
    }
    /** Removes focus from the checkbox. */
    blur() {
        this.input.blur();
    }
    /** Checks for validity but does not show a validation message. Returns `true` when valid and `false` when invalid. */
    checkValidity() {
        return this.input.checkValidity();
    }
    /** Gets the associated form, if one exists. */
    getForm() {
        return this.formControlController.getForm();
    }
    /** Checks for validity and shows the browser's validation message if the control is invalid. */
    reportValidity() {
        return this.input.reportValidity();
    }
    /**
     * Sets a custom validation message. The value provided will be shown to the user when the form is submitted. To clear
     * the custom validation message, call this method with an empty string.
     */
    setCustomValidity(message) {
        this.input.setCustomValidity(message);
        this.formControlController.updateValidity();
    }
    render() {
        return html `
            <div class="container">
                <div class="round">
                    <input
                        id=${this.inputId}
                        part="control"
                        type="checkbox"
                        title=${this
            .title /* An empty title prevents browser validation tooltips from appearing on hover */}
                        name=${this.name}
                        value=${ifDefined(this.value)}
                        .indeterminate=${live(this.indeterminate)}
                        .checked=${live(this.checked)}
                        .disabled=${this.disabled}
                        .required=${this.required}
                        aria-checked=${this.checked ? 'true' : 'false'}
                        @click=${this.handleClick}
                        @input=${this.handleInput}
                        @invalid=${this.handleInvalid}
                        @blur=${this.handleBlur}
                        @focus=${this.handleFocus}
                    />
                    <label for=${this.inputId}> </label>
                </div>
            </div>
            <span part="label" id=${this.labelId} class="checkbox__label">
                <slot></slot>
            </span>
        `;
    }
}
SgdfRoundCheckbox.styles = styles;
__decorate([
    query('input[type="checkbox"]')
], SgdfRoundCheckbox.prototype, "input", void 0);
__decorate([
    state()
], SgdfRoundCheckbox.prototype, "hasFocus", void 0);
__decorate([
    property()
], SgdfRoundCheckbox.prototype, "title", void 0);
__decorate([
    property()
], SgdfRoundCheckbox.prototype, "name", void 0);
__decorate([
    property()
], SgdfRoundCheckbox.prototype, "value", void 0);
__decorate([
    property({ reflect: true })
], SgdfRoundCheckbox.prototype, "size", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SgdfRoundCheckbox.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SgdfRoundCheckbox.prototype, "checked", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SgdfRoundCheckbox.prototype, "indeterminate", void 0);
__decorate([
    defaultValue('checked')
], SgdfRoundCheckbox.prototype, "defaultChecked", void 0);
__decorate([
    property({ reflect: true })
], SgdfRoundCheckbox.prototype, "form", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SgdfRoundCheckbox.prototype, "required", void 0);
__decorate([
    watch('disabled', { waitUntilFirstUpdate: true })
], SgdfRoundCheckbox.prototype, "handleDisabledChange", null);
__decorate([
    watch(['checked', 'indeterminate'], { waitUntilFirstUpdate: true })
], SgdfRoundCheckbox.prototype, "handleStateChange", null);
//# sourceMappingURL=RoundCheckbox.js.map